import styled, { css } from 'styled-components';

import {
    floorGameColor as color,
    CommonAction,
    designColor,
    floorGameDuration as duration,
    floorGameEasing as easing,
    InputNumber,
    recipesCommonAction,
    styledReset,
    styledTypography,
} from '@pkg/primitives';
import { percentToHex } from '@pkg/utils';

import { tokens } from './FieldNumber.tokens';

interface StyledFieldNumberProps {
    $charWidth?: number;
    $size?: number;
    $error?: boolean;
    $stretch?: boolean;
}

const disabledBase = css`
    color: rgba(255, 255, 255, 0.8);
    background-color: rgba(255, 255, 255, 0.06);
`;

const redAlpha20 = `${color.red}${percentToHex(20)}`;

export const StyledFieldNumber = styled.div<StyledFieldNumberProps>`
    ${styledReset.borderBox};

    --field-number-stretch: ${({ $stretch }) => ($stretch ? '1fr' : 'min-content')};
    --field-number-min-width: ${({ $charWidth = tokens.charWidth.preference }) => $charWidth}ch;

    --field-number-size: ${({ $size = tokens.size.preference }) => $size}px;
    --field-number-height: 2em;
    --field-number-radius: 0.5em;

    display: grid;
    grid-template-columns: auto var(--field-number-stretch) auto;
    height: var(--field-number-height);
    font-size: var(--field-number-size);
    color: ${({ $error }) => ($error ? color.red : 'transparent')};
    background-color: ${({ $error }) => ($error ? redAlpha20 : 'transparent')};
    box-shadow: 0 0 0 2px currentColor;
    border-radius: var(--field-number-radius);
    transition-property: color, background-color;
    transition-duration: ${duration.normal}ms;
    transition-timing-function: ${easing.cubic};
`;

export const PrimitiveInputNumber = styled(InputNumber)`
    padding-left: 0.2em;
    padding-right: 0.2em;
    min-width: var(--field-number-min-width);
    width: 100%;
    height: 100%;
    font-size: 1em;
    font-weight: 700;
    color: rgba(255, 255, 255, 1);
    background-color: rgba(255, 255, 255, 0.1);
    outline: 2px solid transparent;
    outline-offset: -1px;
    transition-property: color, background-color, outline-color;
    transition-duration: ${duration.normal}ms;
    transition-timing-function: ${easing.cubic};

    &:hover {
        background-color: rgba(255, 255, 255, 0.2);
    }

    &:focus {
        background-color: rgba(255, 255, 255, 0.14);
        outline-color: ${designColor.brandPurple};
    }

    &:disabled {
        ${disabledBase};
    }
`;

export const Action = styled(CommonAction)`
    ${recipesCommonAction.basic};
    width: var(--field-number-height);
    height: 100%;
    font-size: 1em;
    color: rgba(255, 255, 255, 1);
    background-color: rgba(255, 255, 255, 0.1);
    border-radius: 0;
    outline-offset: -1px;
    // Might be needed to improve mobile device click-and-hold.
    // touch-action: manipulation;

    &:first-child {
        border-top-left-radius: var(--field-number-radius);
        border-bottom-left-radius: var(--field-number-radius);
    }

    &:last-child {
        border-top-right-radius: var(--field-number-radius);
        border-bottom-right-radius: var(--field-number-radius);
    }

    &:hover {
        background-color: rgba(255, 255, 255, 0.2);
    }

    &:active {
        background-color: rgba(255, 255, 255, 0.14);
    }

    &:disabled {
        ${disabledBase};
    }
`;

export const ActionText = styled.span`
    ${styledTypography.allianceBase};
    ${styledTypography.textBoxTrim};
    font-size: inherit;
    font-weight: 700;
    color: currentColor;
`;
